import { ThemeOptions } from '@mui/material';

import { LAST_ROW_ADDITIONAL_PADDING, tableMeasurements } from './constants';
import { tokens } from '../../theme/generated/tokens';
import { DsShadow } from '../../theme/shadows/shadowsTheme';
import { scrollerStyles } from '../scroller';

const topPeripheralsZIndex = 1;

export const tableTheme: ThemeOptions['components'] = {
  DsTable: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const {
          headerFont: _headerFont,
          bodyFont: _bodyFont,
          footerFont: _footerFont,
          headerHeight,
          rowHeight: _rowHeight,
          footerHeight,
          horizontalCellPadding,
          headerIconSize,
          groupHeaderHeight,
        } = tableMeasurements[ownerState.sizing];
        const headerFont = theme.typography[_headerFont];
        const bodyFont = theme.typography[_bodyFont];
        const footerFont = theme.typography[_footerFont];
        const rowHeight = ownerState.rowHeight || _rowHeight;
        const withShadow = !ownerState.noShadow;
        return {
          position: 'relative',
          isolation: 'isolate',
          overflow: 'visible',
          '--rowHeight': `${rowHeight}px`,
          '& .DS-Table-hScroller': {
            filter: 'drop-shadow(0px 1px 1px rgba(109, 127, 136, 0.2))',
          },
          // Floating column styles
          '& .DS-Table-ActionIcon': {
            width: 24,
            height: 24,
            borderRadius: tokens.theme.radius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tokens.theme.bg.structural.surface,
            cursor: 'pointer',
            fontSize: 16,
            '& > .DsIconButton-root': {
              width: 24,
              height: 24,
            },
            '&.DS-Table-actionDisabled': {
              cursor: 'initial',
            },
          },

          '&.DS-Table-floatingColumnSticky': {
            '& .DS-Table-floatingCell': {
              position: 'sticky',
              overflow: 'visible',
              right: 0,
              opacity: 0,
              paddingLeft: 0,
              paddingRight: 10,
              '& .DS-Table-Cell': {
                overflow: 'visible',
                display: 'flex',
                justifyContent: 'flex-end',
              },
              '& .DS-Table-ActionIcon, & .DsIconButton-root': {
                boxShadow: tokens.theme.shadows.elevation.medium,
              },
              '& .DsIconButton-root': {
                backgroundColor: tokens.theme.bg.structural.surface,
              },
            },

            '& .DS-Table-BodyRowWrapper:hover': {
              '& .DS-Table-floatingCell': {
                opacity: 1,
                backgroundColor: 'unset',
              },
            },
          },
          '& .DS-Table-Wrapper': {
            width: '100%',
          },
          '& .DS-Table-HorizontalScroller': {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: 'var(--hScrollerWidth)',
          },
          '& .DS-Table-OuterHeaderWrapper': {
            position: 'relative',
            '&-withGroupHeaders': {
              marginBottom: -groupHeaderHeight,
              zIndex: topPeripheralsZIndex + 1,
            },
          },
          // General row styles
          '& .DS-Table-RowWrapper': {
            position: 'relative',
          },
          // General cell styles
          '& .DS-Table-CellWrapper': {
            alignItems: 'center',
            display: 'flex',
            overflow: 'hidden',
            boxSizing: 'border-box',
            paddingLeft: horizontalCellPadding,
            paddingRight: horizontalCellPadding,
            backgroundColor: tokens.theme.bg.structural.surface,
          },
          '& .DS-Table-lockedCell': {
            position: 'sticky',
            left: `var(--startOffset)`,
            zIndex: 1,
          },
          '& .DS-Table-groupCellLeft': {
            borderLeft: `solid 1px ${tokens.theme.border.structural.neutral}`,
          },
          '& .DS-Table-groupCellRight': {
            borderRight: `solid 1px ${tokens.theme.border.structural.neutral}`,
          },
          '& .DS-Table-groupCellRight + .DS-Table-groupCellLeft': {
            borderLeft: `solid 1px transparent`,
          },
          '& .os-scrollbar-horizontal': {
            paddingLeft: 'var(--lockedColumnsWidth)',
          },
          '& .DS-Table-Cell': {
            overflow: 'hidden',
          },
          '& .DS-Table-stopClickPropagation': {
            cursor: 'initial',
          },
          // Group header
          '& .DS-Table-GroupHeadersWrapper': {
            display: 'flex',
          },
          '& .DS-Table-GroupHeaders': {
            position: 'relative',
            display: 'flex',
            width: 'var(--tableWidth)',
            flexWrap: 'nowrap',
          },
          '& .DS-Table-GroupHeaderWrapper': {
            overflow: 'visible',
            padding: 0,
            // Required for group header offset to work properly
            width: 0,
            position: 'relative',
          },
          '& .DS-Table-LockedGroupHeaders': {
            position: 'sticky',
            minWidth: 0,
            left: 0,
            width: 'var(--lockedColumnsWidth)',
            display: 'flex',
          },
          '& .DS-Table-ScrollableGroupHeaders': {
            width: 'var(--scrollableColumnsWidth)',
            clipPath: 'inset(0 0 0 var(--scrollLeft))',
            display: 'flex',
          },
          '& .DS-Table-loadingRow': {
            height: 'var(--rowHeight)',
          },
          '& .DS-Table-RowSkeletonWrapper': {
            height: 'var(--rowHeight)',
            padding: '11px 32px',
          },
          '& .DS-Table-RowSkeleton': {
            height: '100%',
          },
          '& .DS-Table-CellSkeleton': {
            height: 'calc(var(--rowHeight) - 12px)',
          },
          '& .DS-Table-GroupHeader': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '4px',
            height: groupHeaderHeight,
            padding: '2px 16px',
            backgroundColor: tokens.theme.bg.structural.surface,
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
            borderRadius: '4px 4px 0 0',
            position: 'relative',
            color: tokens.theme.fg.structural.secondary,
            borderBottom: 'none',
            boxSizing: 'border-box',
            ...theme.typography.L10R,
            '&:after': {
              content: '""',
              width: '100%',
              background:
                'linear-gradient(0deg, rgba(8, 41, 57, 0.03) 0%, rgba(8, 41, 57, 0) 72.92%)',
              position: 'absolute',
              inset: 'auto 0 0 0',
              height: '7px',
            },
          },
          '& .DS-Table-TopPeripheralsWrapper': {
            width: '100%',
            overflow: 'hidden',
            zIndex: topPeripheralsZIndex,
          },
          '& .DS-Table-TopPeripherals': {
            minWidth: '100%',
            width: 'var(--tableWidth)',
          },
          '& .DS-Table-LockedColumnsDivider': {
            width: '35px',
            height: '100%',
            borderLeft: '1px solid',
            borderColor: tokens.theme.border.structural.neutralStrong,
            position: 'absolute',
            left: 'var(--lockedColumnsWidth)',
            background:
              'linear-gradient(90deg, rgba(8, 41, 57, 0.05) 0%, rgba(8, 41, 57, 0.00) min(var(--scrollLeft), 72.92%))',
          },
          // Overlay styles
          '& .DS-Table-Overlay': {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 3,
          },
          '& .DS-Table-ResizerLine': {
            display: 'none',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 16,
            background:
              'linear-gradient(180deg, rgba(131, 146, 153, 0.03) 0%, rgba(131, 146, 153, 0.00) 100%)',
            borderLeft: '1px solid',
            borderColor: tokens.theme.border.structural.neutralSubtle,
            '&.DS-Table-resizing': {
              display: 'initial',
            },
          },
          '& .DS-Table-ResizerIconWrapper': {
            height: headerHeight,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 0,
          },
          // Header styles
          '& .DS-Table-HeaderRowWrapper': {
            display: 'flex',
            alignItems: 'stretch',
            minWidth: '100%',
            width: 'max-content',
            borderBottom: '1px solid',
            borderColor: tokens.theme.border.structural.neutralSubtle,
            minHeight: headerHeight,
            borderRadius: '4px 4px 0 0',
            backgroundColor: tokens.theme.bg.structural.surface,
            // clipping overflow is needed for rounded borders to be visible, 'overflow: hidden' would not work with sticky columns as it implicitly creates scroll container
            overflow: 'clip',
          },
          '& .DS-Table-headerHidden': {
            display: 'none',
          },
          '& .DS-Table-HeaderRow': {
            display: 'flex',
            flexDirection: 'row',
            width: 'max-content',
          },
          '& .DS-Table-HeaderCellWrapper-firstGroupColumn': {
            borderLeft: `1px solid ${tokens.theme.border.structural.neutral}`,
          },
          '& .DS-Table-HeaderCellWrapper-lastGroupColumn': {
            borderRight: `1px solid ${tokens.theme.border.structural.neutral}`,
          },
          '& .DS-Table-HeaderCellWrapper-lastGroupColumn + .DS-Table-HeaderCellWrapper-firstGroupColumn':
            {
              borderLeft: 'solid 1px transparent',
            },
          '& .DS-Table-HeaderCellWrapper-lastGroupColumn + .DS-Table-Resizer + .DS-Table-HeaderCellWrapper-firstGroupColumn':
            {
              borderLeft: 'solid 1px transparent',
            },
          '& .DS-Table-HeaderCell-hasInfoTooltip, & .DS-Table-HeaderCell-sortable':
            {
              '& .DS-Table-HeaderCell': {
                display: 'flex',
                alignItems: 'center',
              },
            },
          '& .DS-Table-HeaderCell': headerFont,
          '& .DS-Table-HeaderTitle': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
          '& .DS-Table-InfoIcon': {
            fill: tokens.theme.fg.structural.primary,
          },
          '& * + .Ds-Table-HeaderIcon-root': {
            marginLeft: 4,
          },
          '& .Ds-Table-HeaderIcon-root + .Ds-Table-HeaderIcon-root': {
            marginLeft: 2,
          },
          '& .Ds-Table-HeaderIcon-root': {
            fontSize: headerIconSize,
            minWidth: '1em',
            minHeight: '1em',
          },
          '& .DS-Table-GroupHeaderIcon': {
            fill: tokens.theme.fg.structural.secondary,
            stroke: tokens.theme.fg.structural.secondary,
          },
          // Body styles
          '& .DS-Table-OverlayContainer': {
            position: 'relative',
            ...(withShadow
              ? { boxShadow: theme.shadows[DsShadow.SHADOW] }
              : {}),
          },
          '& .DS-Table-Body': {
            position: 'relative',
            flex: '1 0 auto',
            width: '100%',
            backgroundColor: tokens.theme.bg.structural.surface,
            overflow: 'hidden',
          },
          '&:not(.DS-Table-hasFooter) .DS-Table-Body': {
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
          },
          // Body row styles
          '& .DS-Table-BodyRowWrapper': {
            width: 'var(--tableWidth)',
            '&:not(.DS-Table-firstRow)': {
              borderTop: '1px solid',
              borderColor: tokens.theme.border.structural.neutralSubtle,
            },
            minHeight: ownerState.rowHeight,
            height: 'auto',
            boxSizing: 'border-box',
            '&.DS-Table-lastRow': {
              minHeight: ownerState.rowHeight + LAST_ROW_ADDITIONAL_PADDING, // Row height + scrollbar height
              '& .DS-Table-BodyRow': {
                minHeight: ownerState.rowHeight + LAST_ROW_ADDITIONAL_PADDING,
                '& .DS-Table-BodyCellWrapper': {
                  paddingBottom: LAST_ROW_ADDITIONAL_PADDING, // The height of the scrollbar, prevents scrollbar from covering content
                },
              },
            },
          },
          '& .DS-Table-rowSelectionDisabled': {
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: tokens.theme.disabled.overlay,
              pointerEvents: 'none',
              zIndex: 1,
            },
          },
          '& .DS-Table-BodyRow': {
            minHeight: ownerState.rowHeight,
            display: 'flex',
            flexDirection: 'row',
            width: 'var(--tableWidth)',
            alignItems: 'stretch',
            '&:hover .DS-Table-CellWrapper': {
              backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            },
          },
          '& .DS-Table-ClickableRow': {
            cursor: 'pointer',
          },
          '& .DS-Table-rowExpanded': {
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            '&:not(.DS-Table-firstRow)': {
              borderTop: `1px solid ${tokens.theme.border.structural.neutral}`,
            },
          },
          '& .DS-Table-ExpandedPanelWrapper': {
            overflow: 'hidden',
            height: 'min-content',
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            '&.open': {
              borderTop: `1px solid ${tokens.theme.border.structural.neutral}`,
            },
            '& .DS-Table-CellWrapper': {
              backgroundColor: 'inherit',
            },
          },
          '& .DS-Table-BodyCellWrapper, & .DS-Table-HeaderCellWrapper': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .DS-Table-BodyCell': {
            whiteSpace: 'nowrap',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(typeof bodyFont === 'object'
              ? bodyFont
              : { fontFamily: bodyFont }),
          },
          // Footer styles
          '& .DS-Table-FooterRowWrapper': {
            display: 'flex',
            alignItems: 'stretch',
            width: 'max-content',
            height: footerHeight,
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            borderTop: '1px solid',
            borderColor: tokens.theme.border.structural.neutralSubtle,
            borderRadius: '0 0 4px 4px',
          },
          '& .DS-Table-FooterRow': {
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            width: 'max-content',
          },
          '& .DS-Table-FooterCell': footerFont,
          // Scroll bar styles
          '& .DS-Table-Scroller': {
            ...scrollerStyles(theme),
          },
          // Tree table
          '& .DS-Table-TreeRow-root': {
            position: 'relative',
            backgroundColor: tokens.theme.bg.structural.surface,
          },
          '& .DS-Table-TreeChildRowsWrapper': {
            transition: 'height 100ms',
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            overflow: 'hidden',
            position: 'relative',
            '&-rootLevel:before': {
              content: '""',
              width: '100%',
              background:
                'linear-gradient(180deg, rgba(8, 41, 57, 0.03) 0%, rgba(8, 41, 57, 0) 72.92%)',
              position: 'absolute',
              top: 0,
              height: '15px',
            },
          },
          '& .DS-Table-SubRow': {
            backgroundColor: 'unset',
          },
          '& .DS-Table-TreeRow': {
            position: 'relative',
          },
          '& .DS-TreeTable-ExpanderIcon': {
            position: 'absolute',
            zIndex: 1,
            top: '50%',
            transform: 'translateY(-50%)',
          },
          '& .DS-Table-TreeMarker': {
            position: 'absolute',
            backgroundColor: tokens.theme.bg.structural.surfaceStrong,
            zIndex: 1,
          },
          '& .DS-Table-TreeMarker-vertical': {
            width: 1,
          },
          '& .DS-Table-TreeMarker-horizontal': {
            height: 1,
            top: '50%',
            transform: 'translateY(-50%)',
          },
          '& .DS-Table-DraggableRowContainer': {
            // Populated rows already have their own cell backgrounds (surface);
            // applying a background here leaks through any disabled-row wash
            // (DS-Table-rowSelectionDisabled uses theme.disabled.overlay) and
            // makes the row look dark/washed-out when reorderableRows is
            // combined with isRowSelectable returning false.
            '&:empty': {
              minHeight: 'calc(var(--child-height))',
              boxSizing: 'border-box',
            },
            '& .DS-Table-loadingRow': {
              backgroundColor: tokens.theme.bg.structural.surface,
            },
          },
          '& .DS-Table-DraggableCloneContainer': {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            overflow: 'visible',

            '& .DS-Table-DraggableClone .DS-Table-BodyRow': {
              boxShadow:
                '0px 2px 4px 0px rgba(82, 148, 247, 0.25), 0px -2px 4px 0px rgba(82, 148, 247, 0.15)',
              borderTop: `1px solid ${tokens.theme.border.selection.primary}`,
              borderBottom: `1px solid ${tokens.theme.border.selection.primary}`,
            },
          },
        };
      },
    },
  },
};
